﻿import { Config, EntityDialog, EntityLocalizer, ErrorHandling, Fluent, TranslationConfig, alertDialog, localText, notifySuccess, resolveUrl } from "@serenity-is/corelib";
import { gridDefaults } from "@serenity-is/sleekgrid";
import flatpickr from "flatpickr";
import "flatpickr/dist/l10n";
import { getLanguageList } from "./Helpers/LanguageList";
import { initLightbox } from "./Lightbox";
import DOMPurify from "dompurify";

Config.rootNamespaces.push('WhyGoLess');
TranslationConfig.getLanguageList = getLanguageList;
gridDefaults.sanitizer = (globalThis.DOMPurify = DOMPurify).sanitize;
gridDefaults.enableHtmlRendering = true;
initLightbox();

let culture = (document.documentElement?.lang || 'en').toLowerCase();
if (flatpickr.l10ns[culture]) {
    flatpickr.localize(flatpickr.l10ns[culture]);
} else {
    culture = culture.split('-')[0];
    flatpickr.l10ns[culture] && flatpickr.localize(flatpickr.l10ns[culture]);
}

// Fix for Serenity 9.2.2 EntityLocalizer bug: on entering the Localization tab it tags
// EVERY sibling tool button with `localization-hidden` (regardless of whether the button
// was already hidden by its own visible() condition), then on returning to the form it
// unconditionally reveals all tagged buttons — and never re-runs each button's visible()
// check. Result: undelete/edit/clone (which should stay hidden) appear with no function.
// After the localizer restores buttons, re-apply each tool button's real visible() state.
const origLocalizerUpdateInterface = EntityLocalizer.prototype.updateInterface;
EntityLocalizer.prototype.updateInterface = function (this: any) {
    origLocalizerUpdateInterface.call(this);
    if (this.isEnabled?.() && !this.isLocalizationMode()) {
        (this.options?.getToolButtons?.() ?? []).forEach((el: HTMLElement) =>
            Fluent.trigger(el, "updateInterface", { bubbles: false }));
    }
};

// Serenity confirms a SAVE and says nothing at all about a DELETE.
//
// `showSaveSuccessMessage` calls notifySuccess; `onDeleteSuccess` is an empty method
// and there is no `DeleteSuccessMessage` text to go with it — the framework ships the
// hook and no message in it. So across every dialog in this app the record vanished
// and the only evidence was a grid that had one fewer row, which reads the same as a
// filter that changed and the same as nothing having happened. Undelete is worse: it
// has a button and a confirmation of its own and still answers with silence.
//
// Patched on the prototype rather than in a base class because 33 dialogs extend
// `EntityDialog` directly; giving them all a new base to inherit a two-line method is
// a bigger change than the problem. A subclass that defines its own `onDeleteSuccess`
// still wins, as it did before.
//
// `localText(key, default)` returns the default while the key is absent, so this reads
// in English today and picks up a translation the moment somebody adds one — the same
// route `SaveSuccessMessage` takes.
const origOnDeleteSuccess = EntityDialog.prototype["onDeleteSuccess"];
EntityDialog.prototype["onDeleteSuccess"] = function (this: any, response: any) {
    origOnDeleteSuccess?.call(this, response);
    notifySuccess(localText("Controls.EntityDialog.DeleteSuccessMessage", "Deleted."));
};

// Undelete has no success hook of its own, so the notification rides the caller's
// callback. Wrapped rather than replaced: the base's callback reloads the record, and
// losing that would leave the dialog showing a row it still believed was deleted.
const origUndelete = EntityDialog.prototype["undelete"];
EntityDialog.prototype["undelete"] = function (this: any, callback: (r: any) => void) {
    origUndelete.call(this, (response: any) => {
        callback?.(response);
        notifySuccess(localText("Controls.EntityDialog.UndeleteSuccessMessage", "Restored."));
    });
};

// An expired session must say so and lead back to the login page.
//
// corelib ships the hook — `handleError` asks `Config.notLoggedInHandler` before a
// caller's own `onError` or `errorMode` — and leaves it null, so a lapsed session
// surfaced as "Authorization is required for this request!" on whatever the operator
// pressed, or (before AntiforgeryFailureResultFilter) a bare "HTTP Error 400.", with
// nothing to click but OK. Handled once per lapse: a screen that polls or fires several
// calls at once would otherwise stack one dialog per request. "New tab" is offered
// because the screen behind the dialog may hold work not yet saved — a cart, a half-
// typed form — and signing in elsewhere renews the cookie this tab sends, so the
// action can simply be pressed again.
let sessionExpiredShown = false;
Config.notLoggedInHandler = (_options: any, response: any) => {
    if (sessionExpiredShown)
        return true;

    sessionExpiredShown = true;

    // Path and query only: isSafeReturnUrl refuses a fragment, and falls back to the
    // home page for anything it refuses.
    const loginUrl = resolveUrl("~/Account/Login?returnUrl=" +
        encodeURIComponent(window.location.pathname + window.location.search));

    alertDialog(localText("Site.SessionExpired.Message",
        "Your session has expired, so that last action was not saved. " +
        "Sign in again to carry on."), {
        title: localText("Site.SessionExpired.Title", "Signed out"),
        backdrop: "static",
        buttons: [
            {
                text: localText("Site.SessionExpired.SignIn", "Sign in"),
                cssClass: "btn btn-primary",
                click: () => { window.location.href = loginUrl; }
            },
            {
                text: localText("Site.SessionExpired.NewTab", "Sign in in a new tab"),
                hint: "Keeps this page as it is. Sign in there, come back, and press the action again.",
                cssClass: "btn btn-secondary",
                result: "newtab",
                click: () => { window.open(loginUrl, "_blank"); }
            }
        ],
        onClose: () => { sessionExpiredShown = false; }
    });

    return true;
};

window.onerror = ErrorHandling.runtimeErrorHandler;
window.addEventListener('unhandledrejection', ErrorHandling.unhandledRejectionHandler);
